import { Box } from "@chakra-ui/react";
import { useEffect, useRef } from "react";

interface Props {
  /** Documento completo, já renderizado pela API com os dados do cliente. */
  html: string;
  /** Disparado quando o cliente chega ao fim do texto. */
  onFimDaLeitura: () => void;
  altura?: string;
}

/**
 * Exibe o termo dentro de um iframe.
 *
 * O documento vem da API como HTML completo, com CSS próprio. Renderizar inline
 * misturaria esse CSS com o do portal e mudaria o que o cliente vê — e é justamente o
 * que ele vê que fica provado pelo hash. O iframe isola os dois.
 */
export default function LeitorTermo({ html, onFimDaLeitura, altura }: Props) {
  const ref = useRef<HTMLIFrameElement>(null);

  /**
   * O callback chega como arrow nova a cada render do gate. Guardá-lo num ref é o
   * que impede o efeito de ser refeito quando o pai re-renderiza — e o efeito ser
   * refeito era o bug: a captura de geolocalização re-renderizava o gate, a limpeza
   * tirava o listener de rolagem, e o `load` do iframe não dispara de novo (o
   * documento já estava carregado). Resultado: quem respondesse ao pedido de
   * localização antes de rolar até o fim nunca conseguia habilitar o "Li e aceito".
   */
  const aoFim = useRef(onFimDaLeitura);
  aoFim.current = onFimDaLeitura;

  useEffect(() => {
    const iframe = ref.current;
    if (!iframe) return;

    let janela: Window | null = null;

    function verificarFim() {
      const doc = iframe?.contentDocument;
      if (!doc) return;

      const el = doc.documentElement;
      const rolagem = el.scrollHeight - el.clientHeight;

      // texto que cabe inteiro na tela já está lido
      if (rolagem <= 8) {
        aoFim.current();
        return;
      }

      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
        aoFim.current();
      }
    }

    function aoCarregar() {
      janela = iframe?.contentWindow || null;
      janela?.addEventListener("scroll", verificarFim);
      // documento curto não gera scroll: confere uma vez ao abrir
      verificarFim();
    }

    iframe.addEventListener("load", aoCarregar);

    // Se o efeito rodar com o iframe já carregado, o "load" não vem mais: liga
    // na hora, senão a rolagem deixa de ser observada.
    if (iframe.contentDocument?.readyState === "complete") {
      aoCarregar();
    }

    return () => {
      iframe.removeEventListener("load", aoCarregar);
      janela?.removeEventListener("scroll", verificarFim);
    };
  }, [html]);

  return (
    <Box
      border="1px solid #DDD"
      borderRadius="6px"
      overflow="hidden"
      bgColor="#FFF"
    >
      <iframe
        ref={ref}
        title="Termo"
        srcDoc={html}
        sandbox="allow-same-origin"
        style={{
          width: "100%",
          height: altura || "55vh",
          border: "none",
          display: "block",
        }}
      />
    </Box>
  );
}
